import {MaybeComputedRef, resolveUnref, UseFetchReturn} from "@vueuse/core";
import {computed} from "vue";
import {useCdnFetch, useFetch} from "./fetch";
import {
    ChangeDatePerformTasksRequest,
    PerformTask,
    PhotoToken,
    PhotoAddress,
    UploadRequest,
    UpdatePerformTask,
    SharedPerformTask
} from "../models/performTask";
import {TaskLink} from "../models/task";

export function usePerformTask(projectId: MaybeComputedRef<number>, taskId: MaybeComputedRef<number>): UseFetchReturn<PerformTask> {
    const url = computed(() => {
        const task = resolveUnref(taskId);
        if (task) {
            return `/projects/${resolveUnref(projectId)}/performTasks/${task}`
        } else {
            return ``
        }
    })
    return useFetch<PerformTask>(url, {refetch: () => !!url.value})
        .get()
        .json()
}

export function useSharedPerformTask(token: MaybeComputedRef<string>): UseFetchReturn<SharedPerformTask> {
    const url = computed(() => {
        const task = resolveUnref(token);
        if (task) {
            return `/shared/performTask?token=${task}`
        } else {
            return ``
        }
    })
    return useFetch<SharedPerformTask>(url, {refetch: () => !!url.value})
        .get()
        .json()
}

export function usePerformTaskLink(projectId: MaybeComputedRef<number>, taskId: MaybeComputedRef<number>): UseFetchReturn<TaskLink> {
    const url = computed(() => {
        const task = resolveUnref(taskId);
        if (task) {
            return `/projects/${resolveUnref(projectId)}/performTasks/${task}/link`
        } else {
            return ``
        }
    })
    return useFetch<TaskLink>(url, {refetch: () => !!url.value, immediate: false})
        .get()
        .json()
}
export function useUpdatePerformTask(projectId: MaybeComputedRef<number>, taskId: MaybeComputedRef<number>, update: MaybeComputedRef<UpdatePerformTask>): UseFetchReturn<null> {
    const url = computed(() => {
        const task = resolveUnref(taskId);
        if (task) {
            return `/projects/${resolveUnref(projectId)}/performTasks/${task}`
        } else {
            return ``
        }
    })
    return useFetch<null>(url, {immediate: false})
        .put(update, 'json')

}

export function useUpdatePerformTasks(projectId: MaybeComputedRef<number>, taskId: MaybeComputedRef<number[]>, update: MaybeComputedRef<UpdatePerformTask>): UseFetchReturn<null> {
    const url = computed(() => {
        const params = new URLSearchParams()
        resolveUnref(taskId).forEach(id => params.append('ids', id.toString()))
        const task = resolveUnref(taskId);
        if (task) {
            return `/projects/${resolveUnref(projectId)}/performTasks?${params.toString()}`
        } else {
            return ``
        }
    })
    return useFetch<null>(url, {immediate: false})
        .put(update, 'json')

}

export function useShiftPerformTasks(projectId: MaybeComputedRef<number>, taskId: MaybeComputedRef<number[]>, days: MaybeComputedRef<number>): UseFetchReturn<null> {
    const url = computed(() => {
        const params = new URLSearchParams()
        resolveUnref(taskId).forEach(id => params.append('ids', id.toString()))
        const task = resolveUnref(taskId);
        if (task) {
            return `/projects/${resolveUnref(projectId)}/performTasks/shift?${params.toString()}`
        } else {
            return ``
        }
    })
    const body = computed(() => ({
        days: resolveUnref(days)
    }))
    return useFetch<null>(url, {immediate: false})
        .post(body, 'json')
}

export function useChangeDatePerformTasks(
    projectId: MaybeComputedRef<number>,
    taskIds: MaybeComputedRef<number[]>,
    payload: MaybeComputedRef<ChangeDatePerformTasksRequest>
): UseFetchReturn<null> {
    const url = computed(() => {
        const ids = resolveUnref(taskIds)
        if (!ids || ids.length === 0) {
            return ''
        }

        const params = new URLSearchParams()
        if (ids.includes(-1)) {
            params.append('ids', '-1')
        } else {
            ids.forEach((id) => params.append('ids', id.toString()))
        }

        return `/projects/${resolveUnref(projectId)}/performTasks/changeDate?${params.toString()}`
    })

    return useFetch<null>(url, {immediate: false})
        .post(payload, 'json')
}

export function usePhotoUpload(data: FormData): UseFetchReturn<PhotoAddress> {
    const url = computed(() => 'upload')
    return useCdnFetch<PhotoAddress>(url)
        .post(data)
        .json()
}

export function usePerformTaskPhotoUpload(projectId: MaybeComputedRef<number>, taskId: MaybeComputedRef<number>, request: MaybeComputedRef<UploadRequest>): UseFetchReturn<null> {
    const url = computed(() => {
        const task = resolveUnref(taskId);
        if (task) {
            return `/projects/${resolveUnref(projectId)}/performTasks/${task}/photos`
        } else {
            return ``
        }
    })
    return useFetch<null>(url)
        .post(request, 'json')
}

export function useClosePerformTask(projectId: MaybeComputedRef<number>, taskId: MaybeComputedRef<number>, comment: MaybeComputedRef<string>): UseFetchReturn<null> {
    const url = computed(() => {
        const task = resolveUnref(taskId);
        if (task) {
            return `/projects/${resolveUnref(projectId)}/performTasks/${task}/close`
        } else {
            return ``
        }
    })
    const body = computed(() => ({
        comment: resolveUnref(comment)
    }))
    return useFetch<null>(url, {immediate: false})
        .post(body, 'json')
}

export function useOpenPerformTask(projectId: MaybeComputedRef<number>, taskId: MaybeComputedRef<number>): UseFetchReturn<null> {
    const url = computed(() => {
        const task = resolveUnref(taskId);
        if (task) {
            return `/projects/${resolveUnref(projectId)}/performTasks/${task}/open`
        } else {
            return ``
        }
    })
    return useFetch<null>(url, {immediate: false})
        .post()
}



export function useUploadToken(projectId: MaybeComputedRef<number>, taskId: MaybeComputedRef<number>, captcha: MaybeComputedRef<string | null>): UseFetchReturn<PhotoToken> {
    const url = computed(() => {
        const task = resolveUnref(taskId);
        if (task) {
            return `/projects/${resolveUnref(projectId)}/performTasks/${task}/upload`
        } else {
            return ``
        }
    })
    const body = computed(() => {
        const captchaToken = resolveUnref(captcha);
        if (captchaToken) {
            return {
                token: captchaToken
            }
        } else {
            return null
        }
    })
    return useFetch<PhotoToken>(url, {immediate: false})
        .post(body, 'json')
        .json()
}
